/**
 * Farm-gate console API (uc-core src/modules/gate/*.controller.ts):
 *   GET  /collection-sessions ?groupId&operatorId&status&captureMode&from&to&page&limit
 *   GET  /collection-sessions/:id · /:id/reconciliation · /:id/report · /:id/paper-review
 *   POST /collection-sessions/:id/reconcile
 *   GET  /units ?sessionId&status&hold&farmerId&stream&deliveryUlid&code&cropId&seasonCode&paperDay&page&limit
 *   GET  /units/:id · /units/:id/slip · /units/:id/custody (store)
 *   POST /units/:id/corrections { containerId, grossKg?, sackTypeId?, reason, evidenceMediaId?, disputeExceptionId? }
 *   POST /units/:id/paper-review { result: MATCHES|MISMATCH, field?, slipValue?, typedValue?, note? }
 *   POST /units/:id/hold { reason } · /units/:id/hold/clear { note? } · /units/:id/move-to-ordinary { reason }
 *   POST /containers/:id/hold · /containers/:id/hold/clear
 *   GET  /qr-batches ?issuedToId&kind&groupId · POST /qr-batches/:id/assign|return|void-remainder
 *   GET  /qr-codes/:payload
 *   GET  /receipt-books ?status&issuedToId · GET /receipt-books/:id (with slips) · POST /receipt-books · /:id/issue · /:id/return
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api'
import {
  errorDetail,
  getOne,
  getPage,
  useConsoleMutation,
  usePageQuery,
  type PageParams,
} from './core'
import { ck } from './keys'
import {
  arr,
  bool,
  boolOrNull,
  inrToPaise,
  inrToPaise0,
  iso,
  kgToGrams,
  kgToGrams0,
  num,
  numOrNull,
  obj,
  reasons,
  str,
  strOrNull,
} from './mappers'
import { LIVE_REFRESH_MS } from './openDeliveries'

// ============================== sessions ==============================

export interface SessionFilters extends PageParams {
  groupId?: string
  operatorId?: string
  status?: 'OPEN' | 'CLOSED' | 'RECONCILED' | 'ALL'
  captureMode?: 'APP' | 'PAPER' | 'ALL'
  from?: string
  to?: string
}

export interface SessionRow {
  id: string
  code: string
  status: string
  captureMode: string
  openedAt: string | null
  closedAt: string | null
  reconciledAt: string | null
  groupId: string | null
  villageName: string | null
  operatorId: string | null
  operatorName: string | null
  deviceId: string | null
  lotsMain: number
  lotsConv: number
  netG: number
  openExceptions: number
  tagsBalanced: boolean | null
  sealsBalanced: boolean | null
  bagsBalanced: boolean | null
  allCommandsArrived: boolean | null
  otherOperatorOpenSameDay: boolean
}

export function mapSessionRow(r: Record<string, unknown>): SessionRow {
  return {
    id: str(r.id),
    code: str(r.code),
    status: str(r.status),
    captureMode: str(r.capture_mode, 'APP'),
    openedAt: iso(r.opened_at),
    closedAt: iso(r.closed_at),
    reconciledAt: iso(r.reconciled_at),
    groupId: strOrNull(r.group_id),
    villageName: strOrNull(r.village_name),
    operatorId: strOrNull(r.operator_id),
    operatorName: strOrNull(r.operator_name),
    deviceId: strOrNull(r.device_id),
    lotsMain: num(r.lots_main),
    lotsConv: num(r.lots_conv),
    netG: kgToGrams0(r.net_kg as string),
    openExceptions: num(r.open_exceptions),
    tagsBalanced: boolOrNull(r.tags_balanced),
    sealsBalanced: boolOrNull(r.seals_balanced),
    bagsBalanced: boolOrNull(r.bags_balanced),
    allCommandsArrived: boolOrNull(r.all_commands_arrived),
    otherOperatorOpenSameDay: bool(r.other_operator_open_same_day),
  }
}

export const fetchSessions = (f: SessionFilters) =>
  getPage('/collection-sessions', f, mapSessionRow)

export function useSessions(f: SessionFilters) {
  return usePageQuery([...ck.sessions, 'list', f], () => fetchSessions(f), {
    refetchInterval: LIVE_REFRESH_MS,
  })
}

export interface Reconciliation {
  tagsAtOpen: number
  tagsConfirmed: number
  tagsBound: number
  tagsVoided: number
  tagsCounted: number | null
  sealsAtOpen: number
  sealsConfirmed: number
  sealsBound: number
  sealsVoided: number
  sealsCounted: number | null
  bagsCounted: number
  bagsTagged: number
  commandsExpected: number | null
  commandsArrived: number
  verdict: {
    tagsBalanced: boolean
    sealsBalanced: boolean
    bagsBalanced: boolean
    allCommandsArrived: boolean
    tagsExpectedLeft: number
    sealsExpectedLeft: number
    balanced: boolean
  } | null
}

export function mapReconciliation(raw: unknown): Reconciliation | null {
  const r = obj(raw)
  if (!r.session_id && !r.verdict) return null
  const v = obj(r.verdict)
  return {
    tagsAtOpen: num(r.tags_at_open),
    tagsConfirmed: num(r.tags_confirmed),
    tagsBound: num(r.tags_bound),
    tagsVoided: num(r.tags_voided),
    tagsCounted: numOrNull(r.tags_counted),
    sealsAtOpen: num(r.seals_at_open),
    sealsConfirmed: num(r.seals_confirmed),
    sealsBound: num(r.seals_bound),
    sealsVoided: num(r.seals_voided),
    sealsCounted: numOrNull(r.seals_counted),
    bagsCounted: num(r.bags_counted),
    bagsTagged: num(r.bags_tagged),
    commandsExpected: numOrNull(r.commands_expected),
    commandsArrived: num(r.commands_arrived),
    verdict: r.verdict
      ? {
          tagsBalanced: bool(v.tagsBalanced),
          sealsBalanced: bool(v.sealsBalanced),
          bagsBalanced: bool(v.bagsBalanced),
          allCommandsArrived: bool(v.allCommandsArrived),
          tagsExpectedLeft: num(v.tagsExpectedLeft),
          sealsExpectedLeft: num(v.sealsExpectedLeft),
          balanced: bool(v.balanced),
        }
      : null,
  }
}

export interface SessionLot {
  id: string
  code: string
  stream: string
  status: string
  hold: string
  holdReasons: string[]
  farmerId: string | null
  bagCount: number
  netG: number
  committedAt: string | null
  voidReason: string | null
}
export interface ExceptionLite {
  id: string
  type: string
  severity: string
  status: string
  subjectType: string
  subjectId: string
  raisedAt: string | null
  resolutionCode: string | null
  detail: Record<string, unknown>
}
export const mapExceptionLite = (r: Record<string, unknown>): ExceptionLite => ({
  id: str(r.id),
  type: str(r.type),
  severity: str(r.severity, 'LOW'),
  status: str(r.status),
  subjectType: str(r.subject_type ?? r.subjectType),
  subjectId: str(r.subject_id ?? r.subjectId),
  raisedAt: iso(r.raised_at ?? r.raisedAt),
  resolutionCode: strOrNull(r.resolution_code ?? r.resolutionCode),
  detail: obj(r.detail),
})

export interface SessionDetail {
  id: string
  code: string
  status: string
  captureMode: string
  openedAt: string | null
  closedAt: string | null
  reconciledAt: string | null
  operatorId: string | null
  groupId: string | null
  deviceId: string | null
  testWeight: { expectedKg: string | null; readKg: string | null } | null
  reconciliation: Reconciliation | null
  lots: SessionLot[]
  exceptions: ExceptionLite[]
  raw: Record<string, unknown>
}

export function mapSessionDetail(r: Record<string, unknown>): SessionDetail {
  return {
    id: str(r.id),
    code: str(r.code),
    status: str(r.status),
    captureMode: str(r.capture_mode, 'APP'),
    openedAt: iso(r.opened_at),
    closedAt: iso(r.closed_at),
    reconciledAt: iso(r.reconciled_at),
    operatorId: strOrNull(r.operator_id),
    groupId: strOrNull(r.group_id),
    deviceId: strOrNull(r.device_id),
    testWeight:
      r.test_weight_kg != null || r.test_weight_read_kg != null
        ? {
            expectedKg: strOrNull(r.test_weight_kg),
            readKg: strOrNull(r.test_weight_read_kg),
          }
        : null,
    reconciliation: mapReconciliation(r.reconciliation),
    lots: arr(r.lots).map((l) => ({
      id: str(l.id),
      code: str(l.code),
      stream: str(l.stream),
      status: str(l.status),
      hold: str(l.hold, 'NONE'),
      holdReasons: reasons(l.hold_reason),
      farmerId: strOrNull(l.farmer_id),
      bagCount: num(l.bag_count),
      netG: kgToGrams0(l.net_kg as string),
      committedAt: iso(l.committed_at),
      voidReason: strOrNull(l.void_reason),
    })),
    exceptions: arr(r.exceptions).map(mapExceptionLite),
    raw: r,
  }
}

export function useSession(id: string | undefined) {
  return useQuery({
    queryKey: [...ck.sessions, 'detail', id],
    enabled: Boolean(id),
    queryFn: () => getOne(`/collection-sessions/${id}`, mapSessionDetail),
  })
}

export interface DayReport {
  lots: number
  lotsMain: number
  lotsConv: number
  deliveries: number
  diverts: number
  walkAways: number
  /** Deleted from the phone's open list: wrong farmer, or opened twice. */
  deletedByMistake: number
  netG: number
  netConvG: number
  bags: number
  storageHolds: number
  broughtByOthers: number
  ordinarySharePct: number
  typedTagSharePct: number
  photoOnlyIdentifications: number
  disputes: number
  disputesOpen: number
  disputeSharePct: number
  payoutStatuses: Array<{ status: string; n: number }>
}

export const mapReport = (r: Record<string, unknown>): DayReport => ({
  lots: num(r.lots),
  lotsMain: num(r.lots_main),
  lotsConv: num(r.lots_conv),
  deliveries: num(r.deliveries),
  diverts: num(r.diverts),
  walkAways: num(r.walk_aways),
  deletedByMistake: num(r.deleted_by_mistake),
  netG: kgToGrams0(r.net_kg as string),
  netConvG: kgToGrams0(r.net_kg_conv as string),
  bags: num(r.bags),
  storageHolds: num(r.storage_holds),
  broughtByOthers: num(r.brought_by_others),
  ordinarySharePct: num(r.ordinary_share_pct),
  typedTagSharePct: num(r.typed_tag_share_pct),
  photoOnlyIdentifications: num(r.photo_only_identifications),
  disputes: num(r.disputes),
  disputesOpen: num(r.disputes_open),
  disputeSharePct: num(r.dispute_share_pct),
  payoutStatuses: arr(r.payout_statuses).map((p) => ({ status: str(p.status), n: num(p.n) })),
})

export function useSessionReport(id: string | undefined) {
  return useQuery({
    queryKey: [...ck.sessions, 'report', id],
    enabled: Boolean(id),
    queryFn: () => getOne(`/collection-sessions/${id}/report`, mapReport),
  })
}

export function useReconcileSession() {
  return useConsoleMutation<string, { status: string }>({
    mutationFn: (id) => api.post(`/collection-sessions/${id}/reconcile`),
    invalidate: [ck.sessions, ck.dashboard],
    success: 'Day reconciled',
    errorTitle: 'Cannot reconcile yet',
  })
}

/** Why a CLOSED day cannot be reconciled, read from the reconciliation view (the 409's blockers
 * are not passed through uc-core's error envelope, so the console derives them). */
export function reconcileBlockers(s: SessionDetail): string[] {
  const out: string[] = []
  if (s.status !== 'CLOSED') out.push(`The day is ${s.status}, not CLOSED`)
  const v = s.reconciliation?.verdict
  if (!v) return [...out, 'No reconciliation row yet']
  if (!v.allCommandsArrived) out.push('Not every command from the phone has arrived')
  const unrec = s.exceptions.filter((e) => e.type === 'SESSION_UNRECONCILED')
  if (unrec.some((e) => e.status === 'OPEN'))
    out.push('SESSION_UNRECONCILED is open — resolve or waive it')
  else if ((!v.tagsBalanced || !v.sealsBalanced) && !unrec.length)
    out.push('Tag or seal counts do not balance')
  if (!v.bagsBalanced) {
    const bag = s.exceptions.filter((e) => e.type === 'BAG_COUNT_MISMATCH')
    if (!bag.length || bag.some((e) => e.status === 'OPEN')) out.push('Bag counts do not balance')
  }
  if (s.exceptions.some((e) => e.type === 'TAG_CONFLICT' && e.status === 'OPEN'))
    out.push('A TAG_CONFLICT is open')
  if (s.captureMode === 'PAPER') {
    const mismatch = new Set(
      s.exceptions
        .filter((e) => e.type === 'PAPER_MISMATCH' && e.status === 'OPEN')
        .map((e) => e.subjectId),
    )
    if (s.lots.some((l) => l.status !== 'VOID' && l.hold !== 'NONE' && !mismatch.has(l.id)))
      out.push('Paper-day lots are not all cleared')
  }
  return out
}

// ============================== lots ==============================

export interface LotFilters extends PageParams {
  sessionId?: string
  status?: string
  hold?: 'NONE' | 'QUARANTINE' | 'HELD' | 'RECALLED' | 'ALL'
  farmerId?: string
  stream?: 'MAIN' | 'CONV' | 'ALL'
  code?: string
  cropId?: string
  seasonCode?: string
  paperDay?: boolean
  deliveryUlid?: string
}

export interface LotRow {
  id: string
  code: string
  status: string
  stream: string
  hold: string
  holdReasons: string[]
  farmerId: string | null
  farmerName: string | null
  rosterNo: string | null
  cropId: string | null
  cropName: string | null
  sessionId: string | null
  sessionCode: string | null
  paperDay: boolean
  deliveryUlid: string | null
  bagCount: number
  grossG: number | null
  tareG: number | null
  netG: number | null
  payableG: number | null
  ratePaisePerKg: number | null
  amountPaise: number | null
  moisturePct: string | null
  seasonCode: string | null
  slipNo: number | null
  committedAt: string | null
  voidReason: string | null
}

export function mapLotRow(r: Record<string, unknown>): LotRow {
  return {
    id: str(r.id),
    code: str(r.code),
    status: str(r.status),
    stream: str(r.stream, 'MAIN'),
    hold: str(r.hold, 'NONE'),
    holdReasons: reasons(r.hold_reason),
    farmerId: strOrNull(r.farmer_id),
    farmerName: strOrNull(r.farmer_name),
    rosterNo: strOrNull(r.roster_no),
    cropId: strOrNull(r.crop_id),
    cropName: strOrNull(r.crop_name),
    sessionId: strOrNull(r.session_id),
    sessionCode: strOrNull(r.session_code),
    paperDay: bool(r.paper_day),
    deliveryUlid: strOrNull(r.delivery_ulid),
    bagCount: num(r.bag_count),
    grossG: kgToGrams(r.gross_kg as string),
    tareG: kgToGrams(r.tare_kg as string),
    netG: kgToGrams(r.net_kg as string),
    payableG: kgToGrams(r.payable_kg as string),
    ratePaisePerKg: inrToPaise(r.rate_per_kg as string),
    amountPaise: inrToPaise(r.amount_inr as string),
    moisturePct: strOrNull(r.moisture_pct),
    seasonCode: strOrNull(r.season_code),
    slipNo: numOrNull(r.slip_no),
    committedAt: iso(r.committed_at),
    voidReason: strOrNull(r.void_reason),
  }
}

export const fetchLots = (f: LotFilters) => getPage('/units', f, mapLotRow)
export function useLots(f: LotFilters, enabled = true) {
  return usePageQuery([...ck.lots, 'list', f], () => fetchLots(f), {
    enabled,
    refetchInterval: LIVE_REFRESH_MS,
  })
}

export interface Weight {
  id: string
  kind: string
  valueG: number | null
  value: string | null
  uom: string
  context: string | null
  method: string | null
  takenAt: string | null
  takenById: string | null
  supersedesId: string | null
  photo: string | null
  equipmentId: string | null
  current: boolean
}
const mapWeight = (w: Record<string, unknown>): Weight => {
  const uom = str(w.uom, 'KG')
  return {
    id: str(w.id),
    kind: str(w.kind),
    value: strOrNull(w.value),
    valueG: uom.toUpperCase() === 'KG' ? kgToGrams(w.value as string) : null,
    uom,
    context: strOrNull(w.context),
    method: strOrNull(w.method),
    takenAt: iso(w.taken_at),
    takenById: strOrNull(w.taken_by_id),
    supersedesId: strOrNull(w.supersedes_id),
    photo: strOrNull(w.photo_url),
    equipmentId: strOrNull(w.equipment_id),
    current: w.current === undefined ? true : bool(w.current),
  }
}

export interface Bag {
  id: string
  status: string
  stream: string
  hold: string
  tag: string | null
  conflictTag: string | null
  sackTypeId: string | null
  sackTypeCode: string | null
  grossG: number
  tareG: number
  netG: number
  netNowG: number | null
  convReason: string | null
  weights: Weight[]
}

export interface ClearBlockers {
  openExceptionIds: string[]
  labReportNeeded: boolean
  labReasons: string[]
  otherBlockers: string[]
}
export const mapClearBlockers = (raw: unknown): ClearBlockers | null => {
  if (!raw) return null
  const b = obj(raw)
  return {
    openExceptionIds: arr<string>(b.open_exception_ids).map(String),
    labReportNeeded: bool(b.lab_report_needed),
    labReasons: arr<string>(b.lab_reasons).map(String),
    otherBlockers: arr<string>(b.other_blockers).map(String),
  }
}

export interface LedgerLine {
  id: string
  kind: string
  amountPaise: number
  occurredAt: string | null
  payoutId: string | null
  correctsEntryId: string | null
  note: string | null
  unitCode?: string | null
  unitId?: string | null
}
export interface LotPayout {
  id: string
  kind: string
  amountPaise: number
  status: string
  holdReason: string | null
  attemptNo: number
  utr: string | null
  mode: string | null
  createdAt: string | null
}

export interface LotDetail extends LotRow {
  rateCardId: string | null
  rateCardVersion: number | null
  deductionPct: string | null
  committedById: string | null
  sessionOperatorId: string | null
  slipPhoto: string | null
  deliveredByPhoto: string | null
  bags: Bag[]
  readings: Weight[]
  sample: {
    id: string
    kind: string
    status: string
    weightG: number | null
    collectedAt: string | null
    photo: string | null
    seal: string | null
    conflictSeal: string | null
    retainUntil: string | null
  } | null
  declarations: Array<{
    id: string
    formCode: string
    answers: Record<string, unknown>
    signedAt: string | null
    photo: string | null
  }>
  slip: Array<Record<string, unknown>>
  receiptSlip: {
    id: string
    slipNo: number
    bookNo: string
    status: string
    photo: string | null
    enteredById: string | null
    reviewResult: string | null
    reviewedById: string | null
    reviewedAt: string | null
  } | null
  ledger: LedgerLine[]
  ledgerBalancePaise: number
  payouts: LotPayout[]
  exceptions: ExceptionLite[]
  siblings: Array<{ id: string; code: string; stream: string; status: string; hold: string }>
  clearBlockers: ClearBlockers | null
}

export function mapLotDetail(r: Record<string, unknown>): LotDetail {
  const ledger = arr(r.ledger).map((l): LedgerLine => ({
    id: str(l.id),
    kind: str(l.kind),
    amountPaise: inrToPaise0(l.amount_inr as string),
    occurredAt: iso(l.occurred_at),
    payoutId: strOrNull(l.payout_id),
    correctsEntryId: strOrNull(l.corrects_entry_id),
    note: strOrNull(l.note),
  }))
  const s = r.sample ? obj(r.sample) : null
  const rs = r.receipt_slip ? obj(r.receipt_slip) : null
  const holds = obj(r.holds)
  return {
    ...mapLotRow(r),
    holdReasons: holds.hold_reasons
      ? arr<string>(holds.hold_reasons).map(String)
      : reasons(r.hold_reason),
    rateCardId: strOrNull(r.rate_card_id),
    rateCardVersion: numOrNull(r.rate_card_version),
    deductionPct: strOrNull(r.deduction_pct),
    committedById: strOrNull(r.committed_by_id),
    sessionOperatorId: strOrNull(r.session_operator_id),
    slipPhoto: strOrNull(r.slip_photo_url),
    deliveredByPhoto: strOrNull(r.delivered_by_photo_url),
    bags: arr(r.bags).map((b) => ({
      id: str(b.id),
      status: str(b.status),
      stream: str(b.stream),
      hold: str(b.hold, 'NONE'),
      tag: strOrNull(b.tag),
      conflictTag: strOrNull(b.conflict_qr_payload),
      sackTypeId: strOrNull(b.sack_type_id),
      sackTypeCode: strOrNull(b.sack_type_code),
      grossG: kgToGrams0(b.gross_kg_gate as string),
      tareG: kgToGrams0(b.tare_kg as string),
      netG: kgToGrams0(b.net_kg_gate as string),
      netNowG: kgToGrams(b.net_kg_current as string),
      convReason: strOrNull(b.conv_reason),
      weights: arr(b.weights).map(mapWeight),
    })),
    readings: arr(r.readings).map(mapWeight),
    sample: s
      ? {
          id: str(s.id),
          kind: str(s.kind),
          status: str(s.status),
          weightG: numOrNull(s.weight_g),
          collectedAt: iso(s.collected_at),
          photo: strOrNull(s.photo_url),
          seal: strOrNull(s.seal_payload),
          conflictSeal: strOrNull(s.conflict_qr_payload),
          retainUntil: iso(s.retain_until),
        }
      : null,
    declarations: arr(r.declarations).map((d) => ({
      id: str(d.id),
      formCode: str(d.form_code),
      answers: obj(d.answers),
      signedAt: iso(d.signed_at),
      photo: strOrNull(d.photo_url),
    })),
    slip: arr(r.slip),
    receiptSlip: rs
      ? {
          id: str(rs.id),
          slipNo: num(rs.slip_no),
          bookNo: str(rs.book_no),
          status: str(rs.status),
          photo: strOrNull(rs.photo_url),
          enteredById: strOrNull(rs.entered_by_id),
          reviewResult: strOrNull(rs.review_result),
          reviewedById: strOrNull(rs.reviewed_by_id),
          reviewedAt: iso(rs.reviewed_at),
        }
      : null,
    ledger,
    ledgerBalancePaise: ledger.reduce((sum, l) => sum + l.amountPaise, 0),
    payouts: arr(r.payouts).map((p) => ({
      id: str(p.id),
      kind: str(p.kind),
      amountPaise: inrToPaise0(p.amount_inr as string),
      status: str(p.status),
      holdReason: strOrNull(p.hold_reason),
      attemptNo: num(p.attempt_no, 1),
      utr: strOrNull(p.utr),
      mode: strOrNull(p.mode),
      createdAt: iso(p.created_at),
    })),
    exceptions: arr(r.exceptions).map(mapExceptionLite),
    siblings: arr(r.siblings).map((x) => ({
      id: str(x.id),
      code: str(x.code),
      stream: str(x.stream),
      status: str(x.status),
      hold: str(x.hold, 'NONE'),
    })),
    clearBlockers: mapClearBlockers(holds.clear_blockers),
  }
}

export function useLot(id: string | undefined) {
  return useQuery({
    queryKey: [...ck.lots, 'detail', id],
    enabled: Boolean(id),
    queryFn: () => getOne(`/units/${id}`, mapLotDetail),
  })
}

const LOT_TOUCHED = [ck.lots, ck.stock, ck.payouts, ck.exceptions, ck.dashboard, ck.sessions]

export function useHoldLot() {
  return useConsoleMutation<{ id: string; reason: string; subject?: 'units' | 'containers' }>({
    mutationFn: ({ id, reason, subject = 'units' }) =>
      api.post(`/${subject}/${id}/hold`, { reason }),
    invalidate: LOT_TOUCHED,
    success: 'Put on hold',
    errorTitle: 'Could not put on hold',
  })
}

export function useClearHold(onError?: (e: unknown) => boolean | void) {
  return useConsoleMutation<{ id: string; note?: string; subject?: 'units' | 'containers' }>({
    mutationFn: ({ id, note, subject = 'units' }) =>
      api.post(`/${subject}/${id}/hold/clear`, { note: note || undefined }),
    invalidate: LOT_TOUCHED,
    success: 'Hold cleared',
    errorTitle: 'Hold not cleared',
    onError,
  })
}

export interface MoveToOrdinaryResult {
  unitId: string
  stream: string
  bagsWaitingRedTie: string[]
}
export function useMoveToOrdinary() {
  return useConsoleMutation<{ id: string; reason: string }, MoveToOrdinaryResult>({
    mutationFn: ({ id, reason }) => api.post(`/units/${id}/move-to-ordinary`, { reason }),
    invalidate: LOT_TOUCHED,
    success: (r) =>
      `Moved to ordinary${r?.bagsWaitingRedTie?.length ? ` — ${r.bagsWaitingRedTie.length} bags need a red tie` : ''}`,
    errorTitle: 'Could not move to ordinary',
  })
}

export interface CorrectionBody {
  containerId: string
  grossKg?: string
  sackTypeId?: string
  reason: string
  evidenceMediaId?: string
  disputeExceptionId?: string
}
export interface CorrectionResult {
  correctionEventId: string
  adjustmentInr: string
  payoutAction: string
}
export function useCorrectLot(unitId: string) {
  return useConsoleMutation<CorrectionBody, CorrectionResult>({
    mutationFn: (body) => api.post(`/units/${unitId}/corrections`, body),
    invalidate: LOT_TOUCHED,
    success: 'Correction saved',
    errorTitle: 'Correction not saved',
  })
}

export function useSlip(id: string | undefined) {
  return useQuery({
    queryKey: [...ck.lots, 'slip', id],
    enabled: Boolean(id),
    queryFn: () => api.get<unknown[]>(`/units/${id}/slip`).then((d) => arr(d)),
  })
}

// ============================== paper review ==============================

export interface PaperSlip {
  slipNo: number
  bookNo: string
  bookId: string
  status: string
  photo: string | null
  unitId: string | null
  enteredById: string | null
  enteredAt: string | null
  review: { result: string | null; reviewedById: string | null; reviewedAt: string | null }
  typed: {
    lots: Array<{
      id: string
      code: string
      stream: string
      hold: string
      holdReasons: string[]
      bagCount: number
      grossG: number | null
      tareG: number | null
      netG: number | null
      moisturePct: string | null
      farmerName: string | null
      rosterNo: string | null
      cropName: string | null
    }>
    homeStorage: Record<string, unknown> | null
    bags: Array<{
      id: string
      unitId: string
      tag: string | null
      sackType: string | null
      grossG: number | null
      netG: number | null
      status: string
    }>
    seal: string | null
    sampleWeightG: number | null
    moisture: string | null
  }
  flags: Array<{ id: string; type: string; status: string }>
  reweigh: { bagsTotal: number; bagsReweighed: number; outOfTolerance: string[] }
}
export interface PaperReview {
  sessionId: string
  captureMode: string
  operatorId: string | null
  slips: PaperSlip[]
}

export function mapPaperReview(r: Record<string, unknown>): PaperReview {
  return {
    sessionId: str(r.session_id),
    captureMode: str(r.capture_mode),
    operatorId: strOrNull(r.operator_id),
    slips: arr(r.slips).map((s) => {
      const t = obj(s.typed)
      const rv = obj(s.review)
      const rw = obj(s.reweigh)
      return {
        slipNo: num(s.slip_no),
        bookNo: str(s.book_no),
        bookId: str(s.book_id),
        status: str(s.status),
        photo: strOrNull(s.photo_url),
        unitId: strOrNull(s.unit_id),
        enteredById: strOrNull(s.entered_by_id),
        enteredAt: iso(s.entered_at),
        review: {
          result: strOrNull(rv.result),
          reviewedById: strOrNull(rv.reviewed_by_id),
          reviewedAt: iso(rv.reviewed_at),
        },
        typed: {
          lots: arr(t.lots).map((l) => ({
            id: str(l.id),
            code: str(l.code),
            stream: str(l.stream),
            hold: str(l.hold, 'NONE'),
            holdReasons: reasons(l.hold_reason),
            bagCount: num(l.bag_count),
            grossG: kgToGrams(l.gross_kg as string),
            tareG: kgToGrams(l.tare_kg as string),
            netG: kgToGrams(l.net_kg as string),
            moisturePct: strOrNull(l.moisture_pct),
            farmerName: strOrNull(l.farmer_name),
            rosterNo: strOrNull(l.roster_no),
            cropName: strOrNull(l.crop_name),
          })),
          homeStorage: t.home_storage ? obj(t.home_storage) : null,
          bags: arr(t.bags).map((b) => ({
            id: str(b.id),
            unitId: str(b.unit_id),
            tag: strOrNull(b.tag ?? b.conflict_qr_payload),
            sackType: strOrNull(b.sack_type),
            grossG: kgToGrams(b.gross_kg_gate as string),
            netG: kgToGrams(b.net_kg_gate as string),
            status: str(b.status),
          })),
          seal: strOrNull(t.seal),
          sampleWeightG: numOrNull(t.sample_weight_g),
          moisture: strOrNull(t.moisture),
        },
        flags: arr(s.flags).map((f) => ({
          id: str(f.id),
          type: str(f.type),
          status: str(f.status),
        })),
        reweigh: {
          bagsTotal: num(rw.bags_total),
          bagsReweighed: num(rw.bags_reweighed),
          outOfTolerance: arr<string>(rw.out_of_tolerance).map(String),
        },
      }
    }),
  }
}

export function usePaperReview(sessionId: string | undefined) {
  return useQuery({
    queryKey: [...ck.sessions, 'paper-review', sessionId],
    enabled: Boolean(sessionId),
    queryFn: () => getOne(`/collection-sessions/${sessionId}/paper-review`, mapPaperReview),
  })
}

export interface PaperReviewBody {
  result: 'MATCHES' | 'MISMATCH'
  field?: string
  slipValue?: string
  typedValue?: string
  note?: string
}
export function useReviewPaperSlip() {
  return useConsoleMutation<
    { unitId: string; body: PaperReviewBody },
    { exceptionId?: string | null }
  >({
    mutationFn: ({ unitId, body }) => api.post(`/units/${unitId}/paper-review`, body),
    invalidate: [ck.sessions, ck.lots, ck.exceptions, ck.payouts],
    success: (_d, v) =>
      v.body.result === 'MATCHES' ? 'Marked as matching' : 'PAPER_MISMATCH raised',
    errorTitle: 'Review not saved',
  })
}

// ============================== rolls & codes ==============================

export interface RollFilters extends PageParams {
  issuedToId?: string
  kind?: string
  groupId?: string
}
export interface RollRow {
  id: string
  kind: string
  count: number
  startSequence: number | null
  endSequence: number | null
  issuedToId: string | null
  issuedAt: string | null
  returnedAt: string | null
  groupId: string | null
  villageCode: string | null
  createdAt: string | null
  bound: number
  voided: number
  unused: number
}
export const mapRoll = (r: Record<string, unknown>): RollRow => ({
  id: str(r.id),
  kind: str(r.kind),
  count: num(r.count),
  startSequence: numOrNull(r.start_sequence),
  endSequence: numOrNull(r.end_sequence),
  issuedToId: strOrNull(r.issued_to_id),
  issuedAt: iso(r.issued_at),
  returnedAt: iso(r.returned_at),
  groupId: strOrNull(r.group_id),
  villageCode: strOrNull(r.village_code),
  createdAt: iso(r.created_at),
  bound: num(r.bound),
  voided: num(r.voided),
  unused: num(r.unused),
})
export function rollStatus(r: RollRow): string {
  if (r.returnedAt) return 'RETURNED'
  if (r.unused === 0 && r.count > 0) return 'USED_UP'
  if (r.issuedAt) return 'ISSUED'
  return 'PRINTED'
}

export function useRolls(f: RollFilters) {
  return usePageQuery([...ck.rolls, 'list', f], () => getPage('/qr-batches', f, mapRoll))
}

export function useAssignRoll() {
  return useConsoleMutation<{
    id: string
    /** Omitted in V1: the roll goes to its village. */ toUserId?: string
    spare?: boolean
  }>({
    mutationFn: ({ id, ...body }) => api.post(`/qr-batches/${id}/assign`, body),
    invalidate: [ck.rolls],
    success: 'Roll assigned',
  })
}
export function useReturnRoll() {
  return useConsoleMutation<{
    id: string
    countReceived?: number
    keptByOperator?: boolean
    sessionId?: string
    note?: string
  }>({
    mutationFn: ({ id, ...body }) => api.post(`/qr-batches/${id}/return`, body),
    invalidate: [ck.rolls, ck.sessions, ck.exceptions],
    success: 'Roll return recorded',
  })
}
export function useVoidRemainder() {
  return useConsoleMutation<{ id: string; reason: string }>({
    mutationFn: ({ id, reason }) => api.post(`/qr-batches/${id}/void-remainder`, { reason }),
    invalidate: [ck.rolls],
    success: 'Unused tags voided',
  })
}

export interface CodeLookup {
  id: string
  payload: string
  kind: string
  status: string
  boundType: string | null
  boundId: string | null
  boundAt: string | null
  voidReason: string | null
  batchId: string | null
  issuedToId: string | null
  conflicts: Array<{ type: string; id: string; unitId: string | null }>
  diary: Array<{
    id: string
    bizStep: string
    subjectType: string
    subjectId: string
    occurredAt: string | null
    actorId: string | null
    sessionId: string | null
  }>
}
export function lookupCode(payload: string): Promise<CodeLookup> {
  return getOne(`/qr-codes/${encodeURIComponent(payload.trim())}`, (r) => ({
    id: str(r.id),
    payload: str(r.payload),
    kind: str(r.kind),
    status: str(r.status),
    boundType: strOrNull(r.bound_type),
    boundId: strOrNull(r.bound_id),
    boundAt: iso(r.bound_at),
    voidReason: strOrNull(r.void_reason),
    batchId: strOrNull(r.batch_id),
    issuedToId: strOrNull(r.issued_to_id),
    conflicts: arr(r.conflicts).map((c) => ({
      type: str(c.type),
      id: str(c.id),
      unitId: strOrNull(c.unit_id),
    })),
    diary: arr(r.diary).map((d) => ({
      id: str(d.id),
      bizStep: str(d.biz_step),
      subjectType: str(d.subject_type),
      subjectId: str(d.subject_id),
      occurredAt: iso(d.occurred_at),
      actorId: strOrNull(d.actor_id),
      sessionId: strOrNull(d.session_id),
    })),
  }))
}

// ============================== receipt books ==============================

/** Postgres timestamp text ("2026-10-05 11:06:55.345+05:30", raw SQL rows) → ISO for every browser. */
const pgTime = (v: unknown) => {
  const t = iso(v)
  return t ? t.replace(/^(\d{4}-\d{2}-\d{2}) /, '$1T') : null
}

export interface ReceiptBookRow {
  id: string
  bookNo: string
  firstSlipNo: number
  lastSlipNo: number
  /** PRINTED · ISSUED · RETURNED · VOID */
  status: string
  issuedToId: string | null
  issuedAt: string | null
  returnedAt: string | null
  /** Slips written on (USED) and spoiled — list rows only. */
  used: number
  spoiled: number
}
/** Raw receipt_books row (snake_case); `used` / `spoiled` come with the list. */
export const mapReceiptBook = (r: Record<string, unknown>): ReceiptBookRow => ({
  id: str(r.id),
  bookNo: str(r.book_no ?? r.bookNo),
  firstSlipNo: num(r.first_slip_no ?? r.firstSlipNo),
  lastSlipNo: num(r.last_slip_no ?? r.lastSlipNo),
  status: str(r.status, 'PRINTED'),
  issuedToId: strOrNull(r.issued_to_id ?? r.issuedToId),
  issuedAt: pgTime(r.issued_at ?? r.issuedAt),
  returnedAt: pgTime(r.returned_at ?? r.returnedAt),
  used: num(r.used),
  spoiled: num(r.spoiled),
})

export interface ReceiptSlipRow {
  id: string
  slipNo: number
  /** USED · SPOILED */
  status: string
  unitId: string | null
  sessionId: string | null
  photoRef: string | null
  enteredById: string | null
  enteredAt: string | null
  reviewResult: string | null
  reviewedById: string | null
  reviewedAt: string | null
}
export interface ReceiptBookDetail extends ReceiptBookRow {
  slips: ReceiptSlipRow[]
}
export const mapReceiptBookDetail = (r: Record<string, unknown>): ReceiptBookDetail => {
  const slips = arr(r.slips).map((s): ReceiptSlipRow => ({
    id: str(s.id),
    slipNo: num(s.slip_no),
    status: str(s.status),
    unitId: strOrNull(s.unit_id),
    sessionId: strOrNull(s.session_id),
    photoRef: strOrNull(s.photo_url),
    enteredById: strOrNull(s.entered_by_id),
    enteredAt: pgTime(s.entered_at),
    reviewResult: strOrNull(s.review_result),
    reviewedById: strOrNull(s.reviewed_by_id),
    reviewedAt: pgTime(s.reviewed_at),
  }))
  return {
    ...mapReceiptBook(r),
    used: slips.filter((s) => s.status === 'USED').length,
    spoiled: slips.filter((s) => s.status === 'SPOILED').length,
    slips,
  }
}

export function useReceiptBooks(f: PageParams & { status?: string; issuedToId?: string }) {
  return usePageQuery([...ck.receiptBooks, 'list', f], () =>
    getPage('/receipt-books', f, mapReceiptBook),
  )
}
export function useReceiptBook(id: string | null) {
  return useQuery({
    queryKey: [...ck.receiptBooks, 'one', id],
    enabled: Boolean(id),
    queryFn: () => getOne(`/receipt-books/${id}`, mapReceiptBookDetail),
  })
}
export function useRegisterReceiptBook() {
  return useConsoleMutation<{ bookNo: string; firstSlipNo: number; lastSlipNo: number }>({
    mutationFn: (body) => api.post('/receipt-books', body),
    invalidate: [ck.receiptBooks],
    success: 'Receipt book registered',
    errorTitle: 'Book not registered',
  })
}
export function useIssueReceiptBook() {
  return useConsoleMutation<{ id: string; issuedToId: string }>({
    mutationFn: ({ id, issuedToId }) => api.post(`/receipt-books/${id}/issue`, { issuedToId }),
    invalidate: [ck.receiptBooks],
    success: 'Receipt book issued',
    errorTitle: 'Book not issued',
  })
}
/** 409 lists the slips not yet accounted for: `missingSlips(e)` reads them for the dialog. */
export function useReturnReceiptBook(onError?: (e: unknown) => boolean | void) {
  return useConsoleMutation<{ id: string; blankSlipsInHand?: number[] }>({
    mutationFn: ({ id, blankSlipsInHand }) =>
      api.post(`/receipt-books/${id}/return`, blankSlipsInHand?.length ? { blankSlipsInHand } : {}),
    invalidate: [ck.receiptBooks],
    success: 'Receipt book returned',
    errorTitle: 'Book not returned',
    onError,
  })
}
export function missingSlips(e: unknown): number[] {
  return arr<unknown>(obj(errorDetail(e).errors).missing_slips)
    .map(Number)
    .filter(Number.isFinite)
}
